/**
 * utilities/catalogTaxonomy.js
 * ---------------------------------------------------------------------------
 * How every product form lists categories and units, in one place.
 *
 * The server decorates both lists (GET /category/get-all and GET /unit) with
 * what a picker needs: each category's `group`, `groupLabel` and
 * `groupSortOrder`, and each unit's `familyLabel`, `familySortOrder` and
 * `variantOf`. So these helpers carry no copy of the taxonomy; they only sort,
 * filter and group what the server sent. The taxonomy itself lives in
 * services/catalog/taxonomy.js.
 *
 * Rules every form gets for free:
 *   - "All" and "Random" are never offered (they are not places to file a
 *     product), nor are inactive categories.
 *   - A plural or duplicate unit (Kgs, Pcs, Ltrs ...) is not offered for a new
 *     product; the preferred one is.
 *   - Whatever the product ALREADY has is always offered, so editing an old
 *     product never silently drops its category or unit.
 *   - Typing searches names, old names (aliases) and keywords: "rice" finds
 *     Grains, "stationary" finds Stationery.
 */

const norm = (value) => String(value ?? "").trim().toLowerCase();
const idOf = (value) => String(value?._id ?? value?.value ?? value ?? "");

const SYSTEM = new Set(["all", "random"]);
export const isSystemCategory = (c) => Boolean(c?.isSystem) || SYSTEM.has(norm(c?.category));
const isActiveCategory = (c) => !c?.active_status || c.active_status === "Active";

const OTHER = "Other";

/** Categories a form may offer: active, real, plus whatever is selected. */
export function pickableCategories(categories = [], selected = []) {
  const keep = new Set((Array.isArray(selected) ? selected : [selected]).map(idOf).filter(Boolean));
  return (Array.isArray(categories) ? categories : []).filter(
    (c) => c && (keep.has(String(c._id)) || (isActiveCategory(c) && !isSystemCategory(c)))
  );
}

/**
 * Name or alias contains the text; a keyword starts with it. The server's
 * `searchTerms` (stored plus shipped aliases and keywords) is used when sent.
 */
export function categoryMatches(category, input) {
  const q = norm(input);
  if (!q) return true;
  if (norm(category?.category).includes(q)) return true;
  if ((category?.aliases || []).some((a) => norm(a).includes(q))) return true;
  const terms = Array.isArray(category?.searchTerms) ? category.searchTerms : category?.keywords || [];
  return terms.some((k) => norm(k).startsWith(q));
}

const byRail = (a, b) =>
  (a.groupSortOrder ?? 999) - (b.groupSortOrder ?? 999) ||
  (a.sortOrder ?? 999) - (b.sortOrder ?? 999) ||
  String(a.category || "").localeCompare(String(b.category || ""));

/**
 * Grouped options for react-select: [{ label, options: [category, ...] }].
 * The options are the category objects themselves, so a form's existing
 * getOptionLabel (c.category) and getOptionValue (c._id) keep working.
 */
export function groupCategoryOptions(categories = []) {
  const groups = new Map();
  [...categories].sort(byRail).forEach((c) => {
    const label = c.groupLabel || OTHER;
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(c);
  });
  return [...groups.entries()].map(([label, options]) => ({ label, options }));
}

/** Everything a category picker needs in one call. */
export function categoryPickerOptions(categories = [], { input = "", selected = [] } = {}) {
  return groupCategoryOptions(
    pickableCategories(categories, selected).filter((c) => categoryMatches(c, input))
  );
}

/**
 * A category by name from a spreadsheet cell: exact name first, then an old
 * name or alias, case-insensitively. For the importers.
 */
export function findCategoryByName(categories = [], name) {
  const q = norm(name);
  if (!q) return null;
  const list = Array.isArray(categories) ? categories : [];
  return (
    list.find((c) => norm(c.category) === q) ||
    list.find((c) => (c.aliases || []).some((a) => norm(a) === q)) ||
    null
  );
}

/* ------------------------------------------------------------------------- */
/* Units                                                                      */
/* ------------------------------------------------------------------------- */

export const isVariantUnit = (u) => Boolean(u?.variantOf || u?.preferredUnitId);

/** Units a form may offer: preferred units, plus the product's current one. */
export function pickableUnits(units = [], currentId = null) {
  const current = currentId ? String(currentId) : "";
  return (Array.isArray(units) ? units : []).filter(
    (u) => u && (String(u._id) === current || !isVariantUnit(u))
  );
}

const byFamily = (a, b) =>
  (a.familySortOrder ?? 999) - (b.familySortOrder ?? 999) ||
  (a.sortOrder ?? 999) - (b.sortOrder ?? 999) ||
  String(a.unit || "").localeCompare(String(b.unit || ""), undefined, { sensitivity: "base" });

/**
 * Units grouped by family: [{ label, units: [unit, ...] }]. `toOption` maps a
 * unit to whatever the picker wants; react-select forms pass
 * (u) => ({ value: u._id, label: u.unit }).
 */
export function groupUnits(units = [], { currentId = null, toOption = (u) => u } = {}) {
  const groups = new Map();
  [...pickableUnits(units, currentId)].sort(byFamily).forEach((u) => {
    const label = u.familyLabel || OTHER;
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(toOption(u));
  });
  return [...groups.entries()].map(([label, options]) => ({ label, options }));
}

/** react-select grouped options for a unit picker. */
export const unitSelectOptions = (units = [], currentId = null) =>
  groupUnits(units, {
    currentId,
    toOption: (u) => ({ value: u._id, label: u.unit, name: u.unit }),
  });

/** A unit row by id, from the full list (variants included). */
export const unitById = (units = [], id) =>
  (Array.isArray(units) ? units : []).find((u) => String(u._id) === String(id)) || null;
